/* global globalThis */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ConfirmDialog from '../../components/generics/Confirm/Dialog';
import { errorAlert } from '../../contexts/AlertContext';
import { useTrans } from '../../services/i18n';
import { usersActions } from '../../store/orgUsers';
import { stripCrmEmailLink } from './crmEmailLink';

const DISABLED_ACCESS_MESSAGE = 'User access is disabled for this organization';
const DISABLED_ACCESS_TEXT =
  'You are currently disabled from accessing this organization. Please contact the owner of this organization';
const SWITCH_FAILED_TEXT = 'Failed to switch organization. Please try again.';

export const switchFailureText = (payload) => {
  const text = typeof payload === 'string' ? payload : '';
  if (text.includes(DISABLED_ACCESS_MESSAGE)) return DISABLED_ACCESS_TEXT;
  return text || SWITCH_FAILED_TEXT;
};

const reloadPage = () => globalThis.location.reload();

export default function CrmEmailLinkSwitchDialog({
  link,
  switchOrganization,
  reload = reloadPage,
  notify = errorAlert,
}) {
  const { trans } = useTrans();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [pending, setPending] = useState(false);

  const dismiss = useCallback(() => {
    setOpen(false);
    stripCrmEmailLink(location, history);
  }, [history, location]);

  const requestSwitch = (params) =>
    switchOrganization
      ? switchOrganization(params)
      : dispatch(usersActions.changeActiveOrganization(params)).then((action) => action.payload);

  const confirmSwitch = async () => {
    setPending(true);
    let payload;
    try {
      payload = await requestSwitch({ id: link.organizationId });
    } catch (error) {
      payload = error?.message;
    }
    if (payload?.orgName) {
      reload();
      return;
    }
    notify('error-title', switchFailureText(payload));
    setPending(false);
    dismiss();
  };

  const cancelSwitch = () => {
    if (!pending) dismiss();
  };

  return (
    <ConfirmDialog
      open={open}
      noIcon
      buttonColor="primary"
      title={trans('switch-organization-title')}
      text={trans('crm-email-link-switch-text', { name: link.organizationName })}
      confirmTitle={trans('switch')}
      cancelTitle={trans('stay-here')}
      pending={pending}
      onConfirm={confirmSwitch}
      onClose={cancelSwitch}
    />
  );
}

CrmEmailLinkSwitchDialog.propTypes = {
  link: PropTypes.shape({
    organizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    organizationName: PropTypes.string,
  }).isRequired,
  switchOrganization: PropTypes.func,
  reload: PropTypes.func,
  notify: PropTypes.func,
};
